/**
 * Waktu lokal tenant — satu sumber kebenaran untuk batas "hari ini".
 *
 * Dipakai cron laporan harian (`convex/crons.ts` → `dailyReportSweep`) dan
 * command `/laporan` supaya "hari ini" = hari kalender TOKO, bukan hari server
 * (UTC). Sebelum ini laporan memakai `new Date().setHours(0,0,0,0)` = tengah
 * malam UTC, jadi transaksi 07:00 WIB (00:00 UTC) bisa terhitung "kemarin".
 *
 * Offset di tabel eksplisit: zona yang didukung produk (`tenants.settings.
 * timezone`) adalah WIB/WITA/WIT — Indonesia tidak menganut DST, jadi offset
 * tetap. Tabel dipakai (bukan data tz IANA lewat `Intl`) karena runtime Convex
 * default tidak dijamin menyediakan data timezone, dan tabel ini deterministik
 * + bisa diuji tanpa Intl.
 */

export const TENANT_UTC_OFFSET_MINUTES: Record<string, number> = {
  "Asia/Jakarta": 7 * 60, // WIB
  "Asia/Makassar": 8 * 60, // WITA
  "Asia/Jayapura": 9 * 60, // WIT
};

export const DEFAULT_TENANT_TIMEZONE = "Asia/Jakarta";

export const DEFAULT_DAILY_REPORT_TIME = "18:00";

const DAY_MS = 24 * 60 * 60 * 1000;

const ID_DAYS = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
const ID_MONTHS = [
  "Januari",
  "Februari",
  "Maret",
  "April",
  "Mei",
  "Juni",
  "Juli",
  "Agustus",
  "September",
  "Oktober",
  "November",
  "Desember",
];

// Nama bulan pendek untuk UI/log — mengikuti konvensi tanggal "28 Sep 2026" (AGENTS.md §7.7).
const ID_MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "Mei",
  "Jun",
  "Jul",
  "Agu",
  "Sep",
  "Okt",
  "Nov",
  "Des",
];

/** Offset zona tenant dalam menit; zona tak dikenal → WIB. */
export function tenantOffsetMinutes(timezone?: string): number {
  return (
    TENANT_UTC_OFFSET_MINUTES[timezone ?? ""] ??
    TENANT_UTC_OFFSET_MINUTES[DEFAULT_TENANT_TIMEZONE]
  );
}

/** Geser timestamp ke "epoch bergeser" yang field UTC-nya = waktu lokal tenant. */
export function shiftToLocal(ts: number, timezone?: string): number {
  return ts + tenantOffsetMinutes(timezone) * 60_000;
}

/** Kunci hari lokal tenant, format "YYYY-MM-DD". */
export function tenantDayKey(ts: number, timezone?: string): string {
  return new Date(shiftToLocal(ts, timezone)).toISOString().slice(0, 10);
}

/** Menit sejak tengah malam lokal tenant (0–1439). */
export function minutesOfLocalDay(ts: number, timezone?: string): number {
  const shifted = new Date(shiftToLocal(ts, timezone));
  return shifted.getUTCHours() * 60 + shifted.getUTCMinutes();
}

/** Timestamp UTC saat hari lokal tenant dimulai (00:00 waktu toko). */
export function startOfLocalDay(ts: number, timezone?: string): number {
  const offsetMs = tenantOffsetMinutes(timezone) * 60_000;
  return Math.floor((ts + offsetMs) / DAY_MS) * DAY_MS - offsetMs;
}

/** "18:00" → 1080 menit. Format tidak valid → fallback (default 18:00). */
export function parseReportTime(
  value?: string,
  fallback: string = DEFAULT_DAILY_REPORT_TIME
): number {
  const match = (value ?? "").trim().match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
  if (!match) {
    if (value !== fallback) return parseReportTime(fallback, fallback);
    return 18 * 60;
  }
  return Number(match[1]) * 60 + Number(match[2]);
}

/**
 * Apakah laporan harian tenant harus dikirim sekarang?
 * Due bila jam lokal sudah melewati `settings.dailyReportTime` DAN laporan
 * hari ini belum pernah dikirim (`tenants.lastDailyReportDate`).
 */
export function shouldSendDailyReport(input: {
  now: number;
  timezone?: string;
  reportTime?: string;
  lastDailyReportDate?: string | null;
}): { due: boolean; dayKey: string } {
  const dayKey = tenantDayKey(input.now, input.timezone);
  if (input.lastDailyReportDate === dayKey) return { due: false, dayKey };
  const dueAt = parseReportTime(input.reportTime);
  if (minutesOfLocalDay(input.now, input.timezone) < dueAt) return { due: false, dayKey };
  return { due: true, dayKey };
}

/**
 * Apakah registrasi (wizard onboarding) sudah lewat batas waktunya?
 *
 * Aturan (7 Okt 2026, laporan user): wizard yang DITINGGAL tanpa selesai
 * (botState expired) tidak boleh menggantung selamanya — setelah jendela
 * `REGISTRATION_EXPIRY_MS`, pemilik boleh mulai ulang dari /start (registrasi
 * lama yang KOSONG dihapus). Tenant yang selesai (`completedAt` terisi) TIDAK
 * pernah kedaluwarsa, begitu juga SEMUA tenant yang dibuat sebelum fitur ini
 * (`createdAt` < `ONBOARDING_BACKFILL_TS`, grandfather — akun lama yang belum
 * jualan tidak boleh ikut terhapus).
 */
export const REGISTRATION_EXPIRY_MS = 3 * DAY_MS;

/**
 * Batas grandfather: hanya registrasi yang dibuat SETELAH stempel ini yang
 * bisa kedaluwarsa. Tenant sebelum deploy fitur tidak punya penanda selesai
 * (`onboardingCompletedAt`) padahal sah — tanpa stempel ini mereka nyaris
 * terhapus. Di-set = hari fitur rilis (8 Okt 2026, UTC).
 */
export const ONBOARDING_BACKFILL_TS = Date.UTC(2026, 9, 8);

export function isRegistrationExpired(input: {
  now: number;
  createdAt: number;
  completedAt?: number;
}): boolean {
  if (input.completedAt !== undefined) return false;
  if (input.createdAt < ONBOARDING_BACKFILL_TS) return false;
  return input.now - input.createdAt >= REGISTRATION_EXPIRY_MS;
}

/** Tanggal lokal tenant dalam bahasa Indonesia, mis. "Sabtu, 3 Oktober 2026". */
export function formatLocalDate(ts: number, timezone?: string): string {
  const d = new Date(shiftToLocal(ts, timezone));
  return `${ID_DAYS[d.getUTCDay()]}, ${d.getUTCDate()} ${ID_MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** Label zona singkat untuk UI: WIB (+7) / WITA (+8) / WIT (+9). */
export function tenantZoneLabel(timezone?: string): string {
  const offset = tenantOffsetMinutes(timezone);
  if (offset === 9 * 60) return "WIT";
  if (offset === 8 * 60) return "WITA";
  return "WIB";
}

/**
 * Tanggal + jam lokal tenant untuk log kejadian, mis.
 * "2 Okt 2026 14:05:07 WITA".
 *
 * Zona dihitung dari tabel offset (bukan timezone browser) supaya timestamp
 * deterministik di UI mana pun; detik ikut ditampilkan agar dua kejadian dalam
 * menit yang sama tetap bisa diurutkan.
 */
export function formatLocalDateTime(ts: number, timezone?: string): string {
  const d = new Date(shiftToLocal(ts, timezone));
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getUTCDate()} ${ID_MONTHS_SHORT[d.getUTCMonth()]} ${d.getUTCFullYear()} ` +
    `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())} ` +
    tenantZoneLabel(timezone)
  );
}
